import React from "react";
import LegalLayout from "../../component/Legal/LegalLayout";

const sections = [
  {
    heading: "Introduction and Scope",
    body: (
      <>
        <p>
          DevDesk Integrated Services ("DevDesk", "we", "us" or "our") respects your
          privacy and is committed to protecting your personal data. This Privacy
          Policy explains what personal data we collect through the devdesk.ng
          website (the "Site"), why we collect it, how we use and protect it, and
          the rights available to you under the Nigeria Data Protection Act 2023
          (the "NDPA") and related regulations.
        </p>
        <p className="font-medium text-gray-700">
          This Policy covers the Site only. It does not cover the platforms of the
          independent betting operators that license our software (such as D.Buxton
          United Pools, De-Minister Pools, Lawray Pools Fixed Odds, Eagle United
          Pools, Praise Pools or De-Best Global Fixed Odds). Each operator is a
          separate, independent data controller of the personal data collected on
          its own platform, under its own privacy policy. Where DevDesk processes
          data on an operator's systems, it does so as a service provider on the
          operator's instructions, under contract; requests concerning your data on
          an operator platform should be made to that operator.
        </p>
      </>
    ),
  },
  {
    heading: "Personal Data We Collect",
    body: (
      <>
        <p>We collect the following categories of personal data through the Site:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <span className="font-medium text-gray-700">Contact and enquiry data</span>: your
            name, email address, phone number, company name and the contents of your
            message, when you submit a contact form, request a demo or join a waitlist;
          </li>
          <li>
            <span className="font-medium text-gray-700">Recruitment data</span>: information
            you provide when applying for a role, such as your CV and work history;
          </li>
          <li>
            <span className="font-medium text-gray-700">Technical data</span>: IP address,
            browser type, device information, pages visited and referral source,
            collected automatically through server logs and analytics; and
          </li>
          <li>
            <span className="font-medium text-gray-700">Newsletter data</span>: your email
            address, where you subscribe to updates.
          </li>
        </ul>
        <p>
          The Site is directed at businesses. We do not knowingly collect personal
          data from anyone under the age of 18; if you believe a minor has provided
          us personal data, please contact us and we will delete it.
        </p>
      </>
    ),
  },
  {
    heading: "How and Why We Use Your Data (Lawful Bases)",
    body: (
      <>
        <p>We process personal data only where the NDPA permits, namely:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <span className="font-medium text-gray-700">To respond to you</span>: answering
            enquiries, scheduling demos and following up on requests (performance of
            steps prior to a contract, and our legitimate interest in serving
            prospective clients);
          </li>
          <li>
            <span className="font-medium text-gray-700">To operate and secure the Site</span>:
            monitoring, debugging, preventing abuse and fraud (legitimate interests);
          </li>
          <li>
            <span className="font-medium text-gray-700">To send updates you request</span>:
            newsletters and product announcements (consent, which you may withdraw at
            any time via the unsubscribe link or by contacting us);
          </li>
          <li>
            <span className="font-medium text-gray-700">Recruitment</span>: assessing
            applications (steps prior to a contract of employment); and
          </li>
          <li>
            <span className="font-medium text-gray-700">Legal compliance</span>: complying
            with lawful requests from regulators or courts (legal obligation).
          </li>
        </ul>
        <p>We do not sell personal data, and we do not use it for automated decision-making that produces legal effects concerning you.</p>
      </>
    ),
  },
  {
    heading: "Sharing and Disclosure",
    body: (
      <>
        <p>We share personal data only with:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <span className="font-medium text-gray-700">Service providers</span>: hosting,
            email, analytics and infrastructure vendors who process data on our behalf
            under appropriate contractual safeguards;
          </li>
          <li>
            <span className="font-medium text-gray-700">Professional advisers</span>: lawyers,
            accountants and insurers, where reasonably necessary;
          </li>
          <li>
            <span className="font-medium text-gray-700">Authorities</span>: where required by
            law, court order or a competent regulator; and
          </li>
          <li>
            <span className="font-medium text-gray-700">A successor in business</span>: in
            connection with a merger, acquisition or asset sale, subject to this Policy.
          </li>
        </ul>
      </>
    ),
  },
  {
    heading: "International Transfers",
    body: (
      <p>
        Some of our service providers (for example, cloud hosting and email
        services) may store or process data outside Nigeria. Where personal data is
        transferred out of Nigeria, we take steps to ensure the transfer complies
        with the NDPA, including transferring only to jurisdictions or recipients
        providing an adequate level of protection or under appropriate contractual
        safeguards.
      </p>
    ),
  },
  {
    heading: "Data Retention",
    body: (
      <p>
        We keep personal data only for as long as reasonably necessary for the
        purposes described in this Policy: enquiry records for up to 24 months
        after our last interaction; newsletter data until you unsubscribe;
        recruitment data for up to 12 months after a recruitment decision (unless
        you ask us to retain it longer); and technical logs for up to 12 months.
        We may retain data longer where required by law or to establish, exercise
        or defend legal claims, after which it is securely deleted or anonymised.
      </p>
    ),
  },
  {
    heading: "Security",
    body: (
      <p>
        We apply appropriate technical and organisational measures to protect
        personal data, including encryption in transit (HTTPS), access controls,
        and the principle of least privilege across our systems. No method of
        transmission or storage is completely secure; if we become aware of a
        breach affecting your personal data that is likely to result in a risk to
        you, we will notify the Nigeria Data Protection Commission and affected
        individuals as required by the NDPA.
      </p>
    ),
  },
  {
    heading: "Your Rights Under the NDPA",
    body: (
      <>
        <p>Subject to the conditions in the NDPA, you have the right to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>be informed about, and request access to, the personal data we hold about you;</li>
          <li>request correction of inaccurate or incomplete data;</li>
          <li>request erasure of your data where there is no lawful basis for continued processing;</li>
          <li>object to, or request restriction of, certain processing;</li>
          <li>request a copy of data you provided in a portable format;</li>
          <li>withdraw consent at any time, without affecting the lawfulness of processing before withdrawal; and</li>
          <li>
            lodge a complaint with the Nigeria Data Protection Commission (NDPC) if
            you believe your rights have been infringed.
          </li>
        </ul>
        <p>
          To exercise any of these rights, contact us using the details below. We
          will respond within the timeframes required by the NDPA and may need to
          verify your identity before acting on a request.
        </p>
      </>
    ),
  },
  {
    heading: "Cookies and Analytics",
    body: (
      <p>
        The Site uses strictly necessary cookies and similar technologies for
        basic functionality, and may use analytics to understand how visitors use
        the Site in aggregate. You can control or delete cookies through your
        browser settings; disabling cookies may affect parts of the Site. We do
        not use cookies for cross-site advertising.
      </p>
    ),
  },
  {
    heading: "Changes to This Policy",
    body: (
      <p>
        We may update this Policy from time to time. The current version will
        always be posted on this page with its "Last updated" date. Material
        changes will be highlighted on the Site. Continued use of the Site after
        changes are posted constitutes acceptance of the updated Policy.
      </p>
    ),
  },
  {
    heading: "Contact Us",
    body: (
      <p>
        For privacy enquiries or to exercise your data protection rights, contact
        DevDesk Integrated Services at{" "}
        <a href="mailto:devdesk.services@gmail.com" className="text-[#0186ef] hover:underline">
          devdesk.services@gmail.com
        </a>
        , marking your message "Data Protection". If you are dissatisfied with our
        response, you may complain to the Nigeria Data Protection Commission.
      </p>
    ),
  },
];

const PrivacyPage = () => (
  <LegalLayout
    title="Privacy Policy"
    subtitle="How DevDesk collects, uses and protects personal data on this website, and your rights under the Nigeria Data Protection Act 2023."
    lastUpdated="3 October 2026"
    sections={sections}
  />
);

export default PrivacyPage;
